/**
 * @module services/procedure
 * @description UC-REG-160 — Servicio HTTP del módulo "Gestiones de trámites".
 * Consume los endpoints de /procedures y /procedure-types del backend.
 *
 * Uso:
 * - Paciente/prestador: `mine()`, `create(...)`, `getById(id)`, `getFileSignedUrl(id, key)`.
 * - Admin/manager: todos los anteriores + `list()`, `respond(id, response)`.
 */

import api from "./api";

const procedureService = {
  /**
   * @description Busca pacientes de la organización para el picker
   *              "Nuevo trámite" del staff. Endpoint dedicado — no depende
   *              del permiso `patient.query`. Devuelve identidad completa
   *              (patientId, email, phone).
   * @param {string} q - Al menos 2 caracteres. Match sobre firstName,
   *                     lastName y DNI.
   */
  searchPatients(q) {
    return api
      .get("/procedures/patients-search", { params: { q } })
      .then((r) => r.data);
  },

  /**
   * @description Crea un trámite nuevo con adjuntos. Cuando el caller es
   *              staff (admin/manager), puede incluir `submitterPatientId`
   *              en el FormData para cargarlo en nombre de un paciente.
   * @param {FormData} formData - procedureType, description, files[],
   *                              [submitterPatientId]
   * @returns {Promise<Object>} Procedure creado
   */
  create(formData) {
    return api
      .post("/procedures", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      })
      .then((r) => r.data);
  },

  /**
   * @description Bandeja del admin — todos los trámites de la organización.
   * @param {Object} [params] - { status: 'pending'|'answered' }
   * @returns {Promise<Array>}
   */
  list(params = {}) {
    return api.get("/procedures", { params }).then((r) => r.data);
  },

  /**
   * @description Trámites del usuario logueado (paciente o prestador).
   * @returns {Promise<Array>}
   */
  mine() {
    return api.get("/procedures/mine").then((r) => r.data);
  },

  /**
   * @description Detalle de un trámite.
   * @param {string} id
   * @returns {Promise<Object>}
   */
  getById(id) {
    return api.get(`/procedures/${id}`).then((r) => r.data);
  },

  /**
   * @description Agrega un mensaje al chat del trámite. Válido para
   *              submitter (paciente/prestador) y staff. Cambia status:
   *              staff → answered, submitter → pending.
   * @param {string} id
   * @param {string} text - Puede ser '' si el mensaje solo aporta adjuntos.
   * @param {'email'|'whatsapp'|'none'} [notify='email'] - Canal de
   *        notificación al submitter cuando el autor es staff. Ignorado
   *        cuando el autor es el propio submitter.
   * @param {File[]} [files] - Adjuntos opcionales (0-10 archivos, 15MB c/u).
   * @param {Object} [appointment] - UC-REG-253: turno que el staff carga con
   *        la respuesta. `{ startsAt: ISO UTC, doctor: User._id,
   *        institution?: Institution._id }`. El backend crea un Appointment
   *        sin slot y cancela el vigente anterior si había.
   * @returns {Promise<Object>} trámite actualizado con populate
   */
  addMessage(id, text, notify, files, appointment) {
    const hasFiles = Array.isArray(files) && files.length > 0;
    const appointmentFields = {};
    if (appointment && appointment.startsAt) {
      appointmentFields.appointmentStartsAt = appointment.startsAt;
      if (appointment.doctor) {
        appointmentFields.appointmentDoctor = appointment.doctor;
      }
      if (appointment.institution) {
        appointmentFields.appointmentInstitution = appointment.institution;
      }
    }
    if (!hasFiles) {
      return api
        .post(`/procedures/${id}/messages`, {
          text,
          notify,
          ...appointmentFields,
        })
        .then((r) => r.data);
    }
    const form = new FormData();
    form.append("text", text || "");
    if (notify) form.append("notify", notify);
    Object.keys(appointmentFields).forEach((k) =>
      form.append(k, appointmentFields[k])
    );
    files.forEach((f) => form.append("files", f, f.name));
    return api
      .post(`/procedures/${id}/messages`, form, {
        headers: { "Content-Type": "multipart/form-data" },
      })
      .then((r) => r.data);
  },

  /**
   * @description Staff cierra un trámite. No admite más mensajes después.
   * @param {string} id
   * @returns {Promise<Object>}
   */
  close(id) {
    return api.post(`/procedures/${id}/close`).then((r) => r.data);
  },

  /**
   * @description Devuelve signed URL 60s para descargar un adjunto.
   * @param {string} id - ID del trámite
   * @param {string} key - S3 key del archivo
   * @returns {Promise<{url:string, originalName:string, mimeType:string}>}
   */
  getFileSignedUrl(id, key) {
    return api
      .get(`/procedures/${id}/files/${encodeURIComponent(key)}`)
      .then((r) => r.data);
  },
};

/**
 * @description ABM de tipos de trámite. Admin/manager only.
 */
const procedureTypeService = {
  /**
   * @description Lista tipos. Por default trae solo activos.
   * @param {Object} [params] - { includeInactive: 'true' }
   * @returns {Promise<Array>}
   */
  list(params = {}) {
    return api.get("/procedure-types", { params }).then((r) => r.data);
  },

  /**
   * @description Crea tipo nuevo (admin).
   * @param {Object} data - { name, description, isActive }
   */
  create(data) {
    return api.post("/procedure-types", data).then((r) => r.data);
  },

  /**
   * @description Edita tipo (admin).
   */
  update(id, data) {
    return api.put(`/procedure-types/${id}`, data).then((r) => r.data);
  },

  /**
   * @description Soft delete — setea isActive=false. Preserva referencias.
   */
  remove(id) {
    return api.delete(`/procedure-types/${id}`).then((r) => r.data);
  },
};

export default procedureService;
export { procedureTypeService };
